import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, Users, RefreshCw } from "lucide-react";
import { WinnerCard } from "../components/home/WinnerCard";
import { ComparisonTable } from "../components/home/ComparisonTable";
import { Reviews } from "../components/home/Reviews";
import { FAQ } from "../components/home/FAQ";
import { BottomCTA } from "../components/home/BottomCTA";
import { FAQS } from "../data/comparison";
import { AFFILIATE_URL, AFF_DISCLOSURE, HERO_BG, LAST_UPDATED } from "../lib/constants";
import { trackCta } from "../lib/tracking";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Home() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);
  return (
    <main data-testid="home-page">
      <Helmet>
        <title>AI Voice Generator Comparison 2026: ElevenLabs vs Murf vs PlayHT</title>
        <meta name="description" content="We compared the top 5 AI voice platforms in 2026. See how ElevenLabs, Murf, PlayHT, Lovo and Resemble compare on price, quality, cloning and API — and see our top recommendation." />
        <meta property="og:title" content="AI Voice Generator Comparison 2026: ElevenLabs vs Murf vs PlayHT" />
        <meta property="og:description" content="We compared the top 5 AI voice platforms in 2026. See our top recommendation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voiceranker.ai/" />
        <link rel="canonical" href="https://voiceranker.ai/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[#0F172A] pt-20 pb-32 overflow-hidden" data-testid="hero-section">
        <img src={HERO_BG} alt="Abstract audio waveform visualisation" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#8B7BFF] mb-6 animate-fade-up">
            Last updated: {LAST_UPDATED}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white animate-fade-up" data-testid="hero-title">
            Our Top Recommended AI Voice Platform for 2026: We Compared 5 Leading Options So You Don't Have To
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: "120ms" }}>
            From podcasters to developers to content creators — AI voice tools have exploded in 2026. We compared the five biggest platforms on features, pricing, usability, documentation and market reputation to find the one we recommend for most users. Here's what we found.
          </p>
          <div className="mt-8 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <p className="text-sm text-slate-300 mb-4" data-testid="hero-cta-context">
              Based on our editorial evaluation, ElevenLabs is currently our top recommendation for most users.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={AFFILIATE_URL}
                target="_blank"
                rel="nofollow sponsored noopener"
                onClick={() => trackCta("hero", AFFILIATE_URL)}
                data-testid="cta-hero-elevenlabs"
                className="inline-block bg-[#5A31F4] text-white hover:bg-[#4B29CD] rounded-md px-10 py-4 font-bold text-lg transition-all duration-200 shadow-lg hover:-translate-y-1"
              >
                Try ElevenLabs Free →
              </a>
              <a
                href="#comparison"
                data-testid="hero-compare-link"
                className="inline-block border border-slate-500 text-slate-200 hover:border-white hover:text-white rounded-md px-8 py-4 font-bold text-lg transition-all duration-200"
              >
                Compare All Platforms
              </a>
            </div>
            <p className="mt-3 text-xs text-slate-400 max-w-md mx-auto" data-testid="hero-affiliate-disclosure">{AFF_DISCLOSURE}</p>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-slate-300 font-medium animate-fade-up" style={{ animationDelay: "360ms" }} data-testid="hero-trust-bar">
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#8B7BFF]" /> 10,000+ monthly readers</span>
            <span className="hidden sm:inline text-slate-600">•</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#8B7BFF]" /> Independent editorial comparisons — no paid rankings</span>
            <span className="hidden sm:inline text-slate-600">•</span>
            <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#8B7BFF]" /> Updated monthly with latest pricing</span>
          </div>
        </div>
      </section>

      <WinnerCard />

      {/* Intro copy */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="intro-section">
        <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
          AI voice generation has moved from novelty to necessity. Podcasters are using it to produce episodes in multiple languages. YouTubers are generating voiceovers in minutes. Developers are building entire voice-enabled products on top of these APIs. The question is no longer 'should I use AI voice' — it's 'which platform is actually worth it?'
        </p>
        <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed">
          We compared five platforms across eight criteria: voice quality, cloning capability, language support, API capability, emotion control, real-time generation, pricing transparency, and commercial licensing. The short answer: <strong>ElevenLabs achieved the highest overall score across the evaluation criteria included in our editorial evaluation.</strong> But let's show you the data.
        </p>
      </section>

      <ComparisonTable />
      <Reviews />
      <FAQ />
      <BottomCTA />
    </main>
  );
}
